import { Box, Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { AnimatePresence, motion } from 'framer-motion';

import {
  getPropertiesSectionStatus,
  getSelectedDimension,
  getBaseScenario,
  getSettingsSectionStatus,
} from 'redux/BuilderModeSlice';

import DimensionBuilderModeHeader from './Header';
import BuilderSettingComponent from './Settings';
import BuilderPropertiesComponent from './Properties';
import DimensionBuilderSidebar from './Sidebar';
import NewDimensionBuilderTable from './NewTable';
import NewDimensionBuilderTableOpz from './NewTableopz';

interface Properties {
  flags?: any;
}

const MotionBox = motion(Box);

const PANEL_WIDTH = 310;

// Slide-in/out for the side panels. The panels unmount when closed, so
// AnimatePresence is what makes the *exit* half possible at all. Animating
// width (with the fixed-width card clipped inside) means the table reflows
// alongside the panel rather than the panel overlapping it.
// marginLeft cancels the row's flex gap while collapsed, so the gap fades
// in/out with the panel instead of snapping when it (un)mounts. It must stay
// the negative of that gap — the row uses gap={3} (12px), so -12 here.
const ROW_GAP_PX = 12;
const panelMotion = {
  initial: { width: 0, opacity: 0, marginLeft: -ROW_GAP_PX },
  animate: { width: PANEL_WIDTH, opacity: 1, marginLeft: 0 },
  exit: { width: 0, opacity: 0, marginLeft: -ROW_GAP_PX },
  transition: { duration: 0.24, ease: [0.4, 0, 0.2, 1] },
};

const Dimensions = ({ flags }: Properties) => {
  const selectedDimension = useSelector(getSelectedDimension);
  const selectedScenario = useSelector(getBaseScenario);
  const settingsSectionStatus = useSelector(getSettingsSectionStatus);
  const propertiesSectionStatus = useSelector(getPropertiesSectionStatus);

  return (
    // px={3} = 12px. The secondary header below cancels this with mx='-3' so
    // its divider still runs edge to edge; keep the two in step if either
    // changes, or the header's text stops aligning with the canvas.
    // height 100% is already viewport-derived: the ancestor in
    // ModalOverViewPageHeaderV1/components/TabContent.tsx is sized
    // `calc(100vh - <app header>)`. Restating a raw vh here would subtract the
    // app header twice and push the page's bottom edge off-screen — the same
    // class of bug as the old `calc(100% - 45px)` that clipped the last row.
    <Flex height={'100%'} px={3} pt={0} pb={3}>
      {/* Header stays full width — the side panels live in the row *below* it
          alongside the sidebar/table, so opening one never squeezes the
          secondary header and both panels align to the table's height. */}
      {/* Column flex rather than a hardcoded `calc(100% - <header>px)` on the
          row below: the header's real height (14px padding + 32px controls +
          1px border ≈ 61px) never matched the 45px that was subtracted, so the
          table row was ~16px taller than the space actually left and its bottom
          — the last row's lower half plus the rounded border — fell outside
          this box and got clipped. Letting the row flex into whatever remains
          keeps it exact no matter how the header grows. */}
      <Box width='100%' height='100%' display='flex' flexDirection='column'>
        <DimensionBuilderModeHeader />
        <Flex flex='1' minH={0} width={'100%'} pt={3} gap={3}>
          {selectedScenario && <DimensionBuilderSidebar />}
          {selectedDimension && (
            // flex/minW={0} so the table actually yields width to the
            // Properties panel beside it — its own root is width:100%, which
            // without a shrinkable wrapper pushes the panel past the viewport.
            <Box flex='1' minW={0} height='100%'>
              {flags?.dimensionTableOptimization ? (
                <NewDimensionBuilderTableOpz />
              ) : (
                <NewDimensionBuilderTable />
              )}
            </Box>
          )}
          <AnimatePresence initial={false}>
            {propertiesSectionStatus && (
              <MotionBox
                key='properties-panel'
                height='100%'
                flexShrink={0}
                overflow='hidden'
                {...panelMotion}
              >
                <BuilderPropertiesComponent />
              </MotionBox>
            )}
            {settingsSectionStatus && (
              <MotionBox
                key='settings-panel'
                height='100%'
                flexShrink={0}
                overflow='hidden'
                {...panelMotion}
              >
                <BuilderSettingComponent />
              </MotionBox>
            )}
          </AnimatePresence>
        </Flex>
      </Box>
    </Flex>
  );
};

export default withLDConsumer()(Dimensions);
